
import { useMemo } from 'react';
import { usePropertyBySlug } from './usePropertyBySlug';
import { 
  getPropertySEODataBySlug, 
  generatePropertySEOTitle, 
  generatePropertySEODescription,
  type PropertySEOData 
} from '@/data/propertiesSEO';

export interface PropertySEOInfo {
  title: string;
  description: string;
  canonicalUrl: string;
  image?: string;
  isLoading: boolean;
  hasStaticData: boolean;
}

export const usePropertySEOData = (titleSlug: string): PropertySEOInfo => {
  // Get static SEO data immediately (synchronous)
  const staticSEOData = useMemo(() => {
    return getPropertySEODataBySlug(titleSlug);
  }, [titleSlug]);

  // Get full property data (asynchronous fallback)
  const { data: fullProperty, isLoading } = usePropertyBySlug(titleSlug);

  return useMemo(() => {
    const baseUrl = 'https://estanciacorta.com';
    const canonicalUrl = `${baseUrl}/propiedades/${titleSlug}`;

    // If we have static data, use it immediately
    if (staticSEOData) {
      return {
        title: generatePropertySEOTitle(staticSEOData),
        description: generatePropertySEODescription(staticSEOData),
        canonicalUrl,
        image: staticSEOData.mainImage,
        isLoading: false,
        hasStaticData: true
      };
    }

    // Fallback to full property data if available
    if (fullProperty) {
      const fallbackSEOData: PropertySEOData = {
        id: fullProperty.id,
        slug: titleSlug,
        title: fullProperty.title,
        shortDescription: fullProperty.shortDescription,
        price: fullProperty.price,
        location: fullProperty.location,
        rating: fullProperty.rating,
        guests: fullProperty.guests,
        mainImage: fullProperty.images?.[0]
      };

      return {
        title: generatePropertySEOTitle(fallbackSEOData),
        description: generatePropertySEODescription(fallbackSEOData),
        canonicalUrl,
        image: fallbackSEOData.mainImage,
        isLoading: false,
        hasStaticData: false
      };
    }

    // Loading state or not found
    return {
      title: 'Estancia Corta | Alojamiento en Manzanillo',
      description: 'Encuentra el alojamiento perfecto en Manzanillo, Colima',
      canonicalUrl,
      isLoading,
      hasStaticData: false
    };
  }, [staticSEOData, fullProperty, isLoading, titleSlug]);
};
